import { useCallback, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProductsReport, SalesReport, SummaryReport } from '@teo/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Badge, Panel } from '../components/Panel';
import { EmptyState, TableSkeleton } from '../components/States';
import { useLoad } from '../lib/useAsync';
import { downloadReport, printReport, type SheetSpec } from '../lib/reportExport';
import { bogotaDay, formatDate, formatMoney, formatNumber } from '../lib/format';
import type { Language } from '../i18n';

type Tab = 'summary' | 'products' | 'sales';
const TABS: Tab[] = ['summary', 'products', 'sales'];

function shiftDay(day: string, days: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Vista "Informes y análisis": resumen consolidado, rentabilidad por producto y detalle de ventas (Etapa 3). */
export function Reports() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const { user } = useAuth();
  const today = bogotaDay(new Date());

  const [tab, setTab] = useState<Tab>('summary');
  const [form, setForm] = useState({ from: shiftDay(today, -29), to: today, location_id: '', product: '', brand: '', category: '', user_id: '' });
  const [query, setQuery] = useState<Record<string, string | number | boolean | undefined>>({ from: shiftDay(today, -29), to: today });

  const locations = useLoad(() => api.locations(), []);
  const users = useLoad(() => api.users().catch(() => []), []);
  const summary = useLoad(() => (tab === 'summary' && user ? api.reportSummary(query) : Promise.resolve(null)), [tab, query, user]);
  const products = useLoad(() => (tab === 'products' && user ? api.reportProducts(query) : Promise.resolve(null)), [tab, query, user]);
  const sales = useLoad(() => (tab === 'sales' && user ? api.reportSales(query) : Promise.resolve(null)), [tab, query, user]);

  const money = (n: number) => formatMoney(n, lang);
  /** Pesos enteros para el archivo exportado: el costo medio trae decimales y la pantalla ya los redondea. */
  const cop = (n: number) => Math.round(n);
  const num = (n: number) => formatNumber(n, lang);
  const day = useCallback((v: string) => formatDate(`${v.slice(0, 10)}T12:00:00-05:00`, lang, false), [lang]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setQuery({
      from: form.from, to: form.to,
      location_id: form.location_id === '' ? undefined : Number(form.location_id),
      brand: form.brand.trim() || undefined,
      category: form.category.trim() || undefined,
      user_id: form.user_id === '' ? undefined : Number(form.user_id),
    });
  };
  const all = () => { setQuery({ all: true }); };
  const clear = () => {
    setForm({ from: shiftDay(today, -29), to: today, location_id: '', product: '', brand: '', category: '', user_id: '' });
    setQuery({ from: shiftDay(today, -29), to: today });
  };

  const current = tab === 'summary' ? summary.data : tab === 'products' ? products.data : sales.data;
  const loading = tab === 'summary' ? summary.loading : tab === 'products' ? products.loading : sales.loading;

  /** Encabezado del informe: período, filtros aplicados y quién lo generó. */
  const headerLines = useMemo(() => {
    if (!current) return [] as string[][];
    const f = current.filters;
    const filtros = [
      f.location ? `${t('reports.filters.location')}: ${f.location}` : '',
      f.product ? `${t('reports.filters.product')}: ${f.product}` : '',
      f.brand ? `${t('reports.filters.brand')}: ${f.brand}` : '',
      f.category ? `${t('reports.filters.category')}: ${f.category}` : '',
      f.user ? `${t('reports.filters.user')}: ${f.user}` : '',
    ].filter(Boolean);
    return [
      [t(`reports.tab.${tab}`)],
      [t('reports.period'), current.from === current.to ? day(current.from) : `${day(current.from)} – ${day(current.to)}`],
      [t('reports.filtersApplied'), filtros.length ? filtros.join(' · ') : t('reports.noFilters')],
      [t('reports.generated'), `${formatDate(new Date().toISOString(), lang)} · ${user?.full_name ?? ''}`],
    ];
  }, [current, tab, lang, t, user, day]);

  const exportExcel = () => {
    if (!current) return;
    let sheet: SheetSpec;
    if (tab === 'summary') {
      const s = current as SummaryReport;
      sheet = {
        name: t('reports.tab.summary'), header: headerLines,
        columns: [t('reports.cols.location'), t('reports.cols.units'), t('reports.cols.sales'), t('reports.cols.internalCost'), t('reports.cols.grossMargin'), t('reports.cols.fixed'), t('reports.cols.result'), t('reports.cols.companyProfit')],
        rows: s.locations.map((l) => [l.name, l.units, cop(l.sales), cop(l.internal_cost), cop(l.gross_margin), cop(l.fixed_prorated), cop(l.result), cop(l.company_profit)]),
        totals: [t('reports.cols.total'), s.external.units, cop(s.external.sales), '', '', cop(s.fixed_total), cop(s.company_result), cop(s.external.profit)],
      };
    } else if (tab === 'products') {
      const p = current as ProductsReport;
      sheet = {
        name: t('reports.tab.products'), header: headerLines,
        columns: [t('reports.cols.ref'), t('reports.cols.product'), t('reports.cols.brand'), t('reports.cols.category'), t('reports.cols.units'), t('reports.cols.sales'), t('reports.cols.realCost'), t('reports.cols.profit'), t('reports.cols.marginPct'), t('reports.cols.avgSuggested'), t('reports.cols.avgSold')],
        rows: p.rows.map((r) => [r.ref, r.product_name, r.brand, r.category, r.units, cop(r.sales), cop(r.real_cost), cop(r.profit), r.margin_pct, cop(r.avg_suggested), cop(r.avg_sold)]),
        totals: [t('reports.cols.total'), '', '', '', p.totals.units, cop(p.totals.sales), cop(p.totals.real_cost), cop(p.totals.profit), p.totals.margin_pct, '', ''],
      };
    } else {
      const v = current as SalesReport;
      sheet = {
        name: t('reports.tab.sales'), header: headerLines,
        columns: [t('reports.cols.date'), t('reports.cols.doc'), t('reports.cols.location'), t('reports.cols.user'), t('reports.cols.ref'), t('reports.cols.product'), t('reports.cols.units'), t('reports.cols.suggested'), t('reports.cols.sold'), t('reports.cols.discount'), t('reports.cols.sales'), t('reports.cols.realCost'), t('reports.cols.profit')],
        rows: v.rows.map((r) => [day(r.day), r.doc_no, r.location_name, r.user_name ?? '', r.ref, r.product_name, r.qty, cop(r.suggested_price), cop(r.unit_price), cop(r.discount), cop(r.sales), cop(r.real_cost), cop(r.profit)]),
        totals: [t('reports.cols.total'), '', '', '', '', '', v.totals.units, '', '', cop(v.totals.discount), cop(v.totals.sales), cop(v.totals.real_cost), cop(v.totals.profit)],
      };
    }
    downloadReport([sheet], `TEO_${t(`reports.tab.${tab}`)}_${current.from}_${current.to}.xlsx`.replace(/\s+/g, '_'));
  };

  return (
    <>
      <form className="actions noprint" id="reportFilters" style={{ marginBottom: 12, alignItems: 'end', flexWrap: 'wrap' }} onSubmit={submit}>
        <div className="field">
          <label htmlFor="rpFrom">{t('reports.from')}</label>
          <input className="input" id="rpFrom" type="date" value={form.from} max={form.to} onChange={(e) => setForm({ ...form, from: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="rpTo">{t('reports.to')}</label>
          <input className="input" id="rpTo" type="date" value={form.to} min={form.from} max={today} onChange={(e) => setForm({ ...form, to: e.target.value })} />
        </div>
        <div className="field" style={{ minWidth: 170 }}>
          <label htmlFor="rpLocation">{t('reports.filters.location')}</label>
          <select className="select" id="rpLocation" value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })}>
            <option value="">{t('reports.allLocations')}</option>
            {(locations.data ?? []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="rpBrand">{t('reports.filters.brand')}</label>
          <input className="input" id="rpBrand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor="rpCategory">{t('reports.filters.category')}</label>
          <input className="input" id="rpCategory" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
        </div>
        <div className="field" style={{ minWidth: 170 }}>
          <label htmlFor="rpUser">{t('reports.filters.user')}</label>
          <select className="select" id="rpUser" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}>
            <option value="">{t('reports.allUsers')}</option>
            {(users.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
        </div>
        <button type="submit" className="btn" id="rpApply">{t('reports.apply')}</button>
        <button type="button" className="btn sm" id="rpAll" onClick={all}>{t('reports.all')}</button>
        <button type="button" className="btn sm" id="rpClear" onClick={clear}>{t('reports.clear')}</button>
      </form>

      <div className="actions noprint" style={{ marginBottom: 12, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div className="actions" id="reportTabs">
          {TABS.map((x) => (
            <button key={x} type="button" className={`btn sm${tab === x ? ' black' : ''}`} data-tab={x} onClick={() => setTab(x)}>
              {t(`reports.tab.${x}`)}
            </button>
          ))}
        </div>
        <div className="actions">
          <button type="button" className="btn" id="rpExcel" disabled={!current} onClick={exportExcel}>{t('reports.excel')}</button>
          <button type="button" className="btn" id="rpPdf" disabled={!current} onClick={printReport}>{t('reports.pdf')}</button>
        </div>
      </div>

      <Panel
        title={t(`reports.tab.${tab}`)}
        description={t(`reports.help.${tab}`)}
        badge={current ? <Badge tone="blue">{current.from === current.to ? day(current.from) : `${day(current.from)} – ${day(current.to)}`}</Badge> : null}
      >
        <div className="help" id="reportHeader" style={{ marginBottom: 10 }}>
          {headerLines.slice(1).map((l) => l.join(': ')).join(' · ')}
        </div>

        {loading && !current ? <TableSkeleton cols={6} rows={4} /> : null}

        {tab === 'summary' && summary.data ? (
          <>
            <div className="kpis" style={{ marginBottom: 12 }}>
              <div className="kpi">
                <div className="lab">{t('reports.external')}</div>
                <div className="val" id="rpExternal">{money(summary.data.external.sales)}</div>
                <div className="sub">{t('reports.externalSub', { units: num(summary.data.external.units) })}</div>
              </div>
              <div className="kpi">
                <div className="lab">{t('reports.companyProfit')}</div>
                <div className="val" id="rpProfit">{money(summary.data.external.profit)}</div>
                <div className="sub">{t('reports.companyProfitSub', { pct: num(summary.data.external.margin_pct) })}</div>
              </div>
              <div className="kpi">
                <div className="lab">{t('reports.fixed')}</div>
                <div className="val" id="rpFixed">{money(summary.data.fixed_total)}</div>
                <div className="sub">{t('reports.fixedSub', { days: num(summary.data.days) })}</div>
              </div>
              <div className="kpi">
                <div className="lab">{t('reports.result')}</div>
                <div className="val" id="rpResult">{money(summary.data.company_result)}</div>
                <div className="sub">{t('reports.resultSub')}</div>
              </div>
            </div>
            <div className="tablewrap">
              <table>
                <thead>
                  <tr>
                    <th>{t('reports.cols.location')}</th>
                    <th className="num">{t('reports.cols.units')}</th>
                    <th className="num">{t('reports.cols.sales')}</th>
                    <th className="num">{t('reports.cols.internalCost')}</th>
                    <th className="num">{t('reports.cols.grossMargin')}</th>
                    <th className="num">{t('reports.cols.fixed')}</th>
                    <th className="num">{t('reports.cols.result')}</th>
                  </tr>
                </thead>
                <tbody id="reportSummary">
                  {summary.data.locations.map((l) => (
                    <tr key={l.location_id} data-location={l.code}>
                      <td><b>{l.name}</b><div className="tiny muted">{l.code}</div></td>
                      <td className="num">{num(l.units)}</td>
                      <td className="num" data-col="sales">{money(l.sales)}</td>
                      <td className="num">{money(l.internal_cost)}</td>
                      <td className="num" data-col="margin">{money(l.gross_margin)}</td>
                      <td className="num">{money(l.fixed_prorated)}</td>
                      <td className={`num ${l.result < 0 ? 'auditneg' : 'auditpos'}`} data-col="result">{money(l.result)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="help" id="reportInternalNote" style={{ marginTop: 10 }}>
              {t('reports.internalNote', { count: num(summary.data.internal.count), total: money(summary.data.internal.total) })}
            </p>
          </>
        ) : null}

        {tab === 'products' && products.data ? (
          products.data.rows.length === 0 ? <EmptyState icon="inbox" title={t('reports.empty')} /> : (
            <div className="tablewrap">
              <table>
                <thead>
                  <tr>
                    <th>{t('reports.cols.ref')}</th>
                    <th>{t('reports.cols.product')}</th>
                    <th className="num">{t('reports.cols.units')}</th>
                    <th className="num">{t('reports.cols.sales')}</th>
                    <th className="num">{t('reports.cols.realCost')}</th>
                    <th className="num">{t('reports.cols.profit')}</th>
                    <th className="num">{t('reports.cols.marginPct')}</th>
                    <th className="num">{t('reports.cols.avgSuggested')}</th>
                    <th className="num">{t('reports.cols.avgSold')}</th>
                  </tr>
                </thead>
                <tbody id="reportProducts">
                  {products.data.rows.map((r) => (
                    <tr key={r.product_id} data-ref={r.ref}>
                      <td><b>{r.ref}</b></td>
                      <td>{r.product_name}<div className="tiny muted">{[r.brand, r.category].filter(Boolean).join(' · ')}</div></td>
                      <td className="num">{num(r.units)}</td>
                      <td className="num" data-col="sales">{money(r.sales)}</td>
                      <td className="num">{money(r.real_cost)}</td>
                      <td className={`num ${r.profit < 0 ? 'auditneg' : 'auditpos'}`} data-col="profit">{money(r.profit)}</td>
                      <td className="num">{t('pos.percent', { n: num(r.margin_pct) })}</td>
                      <td className="num">{money(r.avg_suggested)}</td>
                      <td className="num" data-col="avgSold">{money(r.avg_sold)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={2}><b>{t('reports.cols.total')}</b></td>
                    <td className="num"><b>{num(products.data.totals.units)}</b></td>
                    <td className="num" data-col="totalSales"><b>{money(products.data.totals.sales)}</b></td>
                    <td className="num"><b>{money(products.data.totals.real_cost)}</b></td>
                    <td className="num"><b>{money(products.data.totals.profit)}</b></td>
                    <td className="num"><b>{t('pos.percent', { n: num(products.data.totals.margin_pct) })}</b></td>
                    <td colSpan={2}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )
        ) : null}

        {tab === 'sales' && sales.data ? (
          sales.data.rows.length === 0 ? <EmptyState icon="inbox" title={t('reports.empty')} /> : (
            <>
              <div className="tablewrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t('reports.cols.date')}</th>
                      <th>{t('reports.cols.doc')}</th>
                      <th>{t('reports.cols.location')}</th>
                      <th>{t('reports.cols.user')}</th>
                      <th>{t('reports.cols.product')}</th>
                      <th className="num">{t('reports.cols.units')}</th>
                      <th className="num">{t('reports.cols.suggested')}</th>
                      <th className="num">{t('reports.cols.sold')}</th>
                      <th className="num">{t('reports.cols.sales')}</th>
                      <th className="num">{t('reports.cols.profit')}</th>
                    </tr>
                  </thead>
                  <tbody id="reportSales">
                    {sales.data.rows.map((r) => (
                      <tr key={r.line_id} data-doc={r.doc_no}>
                        <td>{day(r.day)}</td>
                        <td>{r.doc_no}</td>
                        <td>{r.location_name}</td>
                        <td>{r.user_name ?? ''}</td>
                        <td><b>{r.ref}</b> {r.product_name}</td>
                        <td className="num">{num(r.qty)}</td>
                        <td className="num" data-col="suggested">{money(r.suggested_price)}</td>
                        <td className="num" data-col="sold">
                          {money(r.unit_price)}
                          {r.negotiated ? <div className="tiny"><Badge tone="amber">{t('reports.negotiated')}</Badge></div> : null}
                        </td>
                        <td className="num" data-col="sales">{money(r.sales)}</td>
                        <td className={`num ${r.profit < 0 ? 'auditneg' : 'auditpos'}`}>{money(r.profit)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={5}><b>{t('reports.cols.total')}</b></td>
                      <td className="num"><b>{num(sales.data.totals.units)}</b></td>
                      <td colSpan={2} className="num"><b>{t('reports.discountTotal', { amount: money(sales.data.totals.discount) })}</b></td>
                      <td className="num" data-col="totalSales"><b>{money(sales.data.totals.sales)}</b></td>
                      <td className="num"><b>{money(sales.data.totals.profit)}</b></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {sales.data.total_rows > sales.data.rows.length ? (
                <p className="help" id="reportMore" style={{ marginTop: 10 }}>
                  {t('reports.more', { shown: num(sales.data.rows.length), total: num(sales.data.total_rows) })}
                </p>
              ) : null}
            </>
          )
        ) : null}
      </Panel>
    </>
  );
}
